import React from 'react';
import { object, string, array, bool } from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { parse } from 'libphonenumber-js';
import { compose, head, isEmpty, isNil, partial, values, startsWith, replace } from 'ramda';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import PhoneIcon from '@material-ui/icons/Phone';
import { styled } from '@material-ui/core';

import CountrySelector from './CountrySelector/CountrySelector';
import { RequiredSpan } from '../TextPicker/TextPicker';

import { formatMessage, flipPick, toArray } from '../../../utils/helpers';
import { parsePhoneNumber, PARSE_TYPE } from '../../../utils/normalizing';
import { isValidPhoneNumber, REQUIRED_ERROR } from '../../../utils/validation';
import generalMessages from '../../../messages';
import messages from './messages';
import { useConnect } from './hooks';

const CountrySelectorGrid = styled(Grid)(({ isDownsized }) => ({
  position: 'relative',
  '& .react-tel-input': {
    position: 'static !important',
  },
  '& .flag-dropdown': {
    top: '25px !important',
    bottom: '10px !important',
    background: 'none !important',
  },
  '& .open-dropdown': {
    width: '100% !important',
  },
  '& .country-list': {
    position: 'absolute !important',
    width: 'calc(100% - 16px) !important',
    margin: '0 !important',
    height: isDownsized ? '240px !important' : '280px !important',
  },
  '& .MuiInputLabel-root': {
    transform: 'translate(50px, 22px)',
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(0, 0) scale(0.75)',
  },
}));

const PhoneNumber = ({
  className,
  style,
  country,
  formik,
  id,
  intl,
  label,
  labelStyle,
  iconHidden,
  required,
  shrink = true,
  preferredCountries = [],
}) => {
  const { isDownsized } = useConnect();
  const getIntlMessage = partial(formatMessage, [intl]);
  const placeholder = compose(getIntlMessage, compose(head, values, flipPick(messages), toArray))(country);

  const isError = field => {
    if (formik.touched[field] || !isEmpty(formik.values[field])) {
      return formik.errors[field];
    }
    return undefined;
  };

  const onCountryChange = phoneCountry => {
    if (country !== phoneCountry) {
      formik.setFieldValue('phoneCountry', phoneCountry);
      formik.setFieldValue('phoneNumber', '');
    }
  };

  const normalize = value => {
    const phoneNumber = startsWith('00', value) ? replace(/00/, '+', value) : value;
    const phoneCountryCode = parse(phoneNumber)?.country?.toLowerCase() || country;
    const isValid = isValidPhoneNumber(phoneNumber, phoneCountryCode);
    formik.setFieldValue('phoneCountry', phoneCountryCode);
    return isValid ? parsePhoneNumber(phoneNumber, phoneCountryCode, PARSE_TYPE.INTERNATIONAL) : phoneNumber;
  };

  return (
    <Grid
      container
      alignItems={isError(id) ? 'center' : 'flex-end'}
      className={className}
      style={style}
      justify="space-between"
      spacing={2}
    >
      {!iconHidden && (
        <Grid item style={{ paddingTop: '9px' }}>
          <PhoneIcon fontSize="small" className="icon icon-field" color="secondary" />
        </Grid>
      )}
      <CountrySelectorGrid item container alignItems="center" xs isDownsized={isDownsized}>
        <CountrySelector formik={formik} onChange={onCountryChange} value={country} preferredCountries={preferredCountries} />
        <TextField
          error={!isNil(isError(id))}
          fullWidth
          helperText={
            isError(id) &&
            (isError(id) === REQUIRED_ERROR ? (
              <FormattedMessage {...generalMessages[isError(id)]} />
            ) : (
              <FormattedMessage {...messages[isError(id)]} />
            ))
          }
          id={id}
          InputLabelProps={shrink ? { shrink: true, style: labelStyle } : null}
          label={
            <>
              {getIntlMessage(label || messages.label)}
              {required && <RequiredSpan />}
            </>
          }
          onBlur={e => {
            e.target.value = normalize(e.target.value);
            formik.handleBlur(e);
            formik.handleChange(e);
          }}
          onChange={formik.handleChange}
          placeholder={placeholder}
          type="tel"
          value={formik.values[id]}
        />
      </CountrySelectorGrid>
    </Grid>
  );
};

PhoneNumber.propTypes = {
  className: string,
  style: object,
  country: string.isRequired,
  formik: object,
  id: string,
  intl: object,
  label: object,
  labelStyle: object,
  iconHidden: bool,
  required: bool,
  shrink: bool,
  preferredCountries: array,
};

export default injectIntl(PhoneNumber);
